import { createRoot } from "solid-js";
import { createStore } from "solid-js/store";
import type { TrackingEnvironment } from "./types";

/**
 * Tracking configuration as the server hands it over — the <c>tracking</c> object on
 * <c>/api/tenant/config</c>, which is resolved from the request hostname against the tenant
 * register.
 *
 * <p>Every field is optional and absence means off. The asymmetry is deliberate: a tenant with no
 * tracking section, a half-written one, and a config request that failed outright all have to land
 * on <c>TRACKING_OFF</c> — never on a state that sends something somewhere. The tenant store merges
 * the server answer over its defaults the same way (themeStore), so a field the server omits is a
 * field that stays off rather than one that becomes undefined and reads as truthy later.</p>
 *
 * <p>Container, measurement and pixel ids are read from here and from nowhere else. None of them may
 * come from a query parameter: whoever names the property owns the events and the audiences built
 * from them, so a link author must never be able to point this app at a container of their own.</p>
 */
export interface TrackingConfig {
  /** Global kill switch. Everything else is inert while this is not exactly <c>true</c>. */
  enabled?: boolean;
  /** GTM web container, e.g. "GTM-5QRKHHXW". Nothing loads and nothing is pushed without it. */
  gtmContainerId?: string | null;
  /** This tenant's own GA4 property. A tenant's configuration never carries another tenant's. */
  ga4MeasurementId?: string | null;
  /**
   * LOBAS' own GA4 property, which a white-label deployment's events reach in addition to the
   * tenant's own. Absent means no central destination at all — and the server already resolves the
   * three ways that happens (nothing configured, the flag below off, or this tenant's own property
   * being the central one), so the client never has to decide it.
   */
  centralGa4MeasurementId?: string | null;
  /**
   * Whether this tenant's measurement also reaches LOBAS' accounts. The same switch the server-side
   * pipeline reads, meaning the same thing here: false silences LOBAS' copy in the browser too.
   */
  centralAnalyticsEnabled?: boolean;
  googleAdsConversionId?: string | null;
  /** Conversion label per event name, e.g. <c>{ generate_lead: "AbC-D_efG" }</c>. */
  adsLabels?: Record<string, string> | null;
  metaPixelId?: string | null;
  tiktokPixelId?: string | null;
  /** Which vendors this deployment actually feeds. An id present but the vendor absent means off. */
  activeVendors?: TrackingVendor[] | null;
  /** Bumped when the consent text changes materially; the CMP re-asks when it moves past what was stored. */
  consentPolicyVersion?: number | null;
  privacyPolicyUrl?: string | null;
  cookiePolicyUrl?: string | null;
  /** Which deployment this is. Unrecognised or absent blocks tracking — see <c>environmentRecognised</c>. */
  environment?: TrackingEnvironment | null;
  /** Hostnames this configuration may fire on. Empty/absent makes no claim; a non-empty list is binding. */
  allowedHostnames?: string[] | null;
}

export type TrackingVendor = "ga4" | "googleAds" | "meta" | "tiktok";

// The environment vocabulary belongs to the event contract (types.ts), not to this file — it rides
// on every event as `environment`, so a value that is legal here and unknown there would be a row
// nothing can filter on.
export type { TrackingEnvironment };

const ENVIRONMENTS: readonly TrackingEnvironment[] = ["development", "test", "production"];

/**
 * A container id has to look like one before it reaches a script src. The id is server-side data
 * rather than user input, but it crosses into a URL the browser then executes, and a typo that
 * happens to carry a slash or a query character would build a different URL than the one intended.
 */
const CONTAINER_ID_PATTERN = /^GTM-[A-Z0-9]{4,}$/;

/**
 * The same argument for a measurement id. The negative lookahead rejects the <c>G-XXXXXXXXXX</c>
 * placeholder the seeded register ships, which satisfies the shape and names no property —
 * the container's own guard spells the identical rule, and the two have to agree or one of them
 * would send where the other refused.
 */
const MEASUREMENT_ID_PATTERN = /^G-(?!X+$)[A-Z0-9]{6,}$/;

/** The state every unconfigured, misconfigured and failed load resolves to. Sends nothing. */
export const TRACKING_OFF: TrackingConfig = {
  enabled: false,
  gtmContainerId: null,
  ga4MeasurementId: null,
  centralGa4MeasurementId: null,
  // False rather than the server's "absent means true": off is what an unconfigured, half-written
  // or failed load has to mean, and the default here is the value all three land on.
  centralAnalyticsEnabled: false,
  googleAdsConversionId: null,
  adsLabels: null,
  metaPixelId: null,
  tiktokPixelId: null,
  activeVendors: null,
  consentPolicyVersion: null,
  privacyPolicyUrl: null,
  cookiePolicyUrl: null,
  environment: null,
  allowedHostnames: null,
};

// ── Misconfiguration reporting ───────────────────────────────────────────────

const warned = new Set<string>();

/**
 * Configuration mistakes are reported once and out loud, not behind a DEV guard. A deployment that
 * believes it is tracking and silently is not is the failure worth catching, and that one is only
 * ever visible in a production console.
 */
function warnOnce(key: string, message: string): void {
  if (warned.has(key)) return;
  warned.add(key);
  console.warn("[tracking] " + message);
}

// ── Store ────────────────────────────────────────────────────────────────────

interface TrackingConfigState {
  config: TrackingConfig;
  /** The server has answered. Distinct from "tracking is on" — most answers say nothing at all. */
  loaded: boolean;
}

function createTrackingConfigStore() {
  const [state, setState] = createStore<TrackingConfigState>({
    config: { ...TRACKING_OFF },
    loaded: false,
  });

  function setConfig(config: TrackingConfig | null | undefined) {
    // Merged over TRACKING_OFF, so a field the server leaves out is off rather than undefined.
    setState({ config: { ...TRACKING_OFF, ...(config ?? {}) }, loaded: true });
  }

  return { state, setConfig };
}

// ── Singleton ────────────────────────────────────────────────────────────────

const configStore = createRoot(createTrackingConfigStore);

export const trackingConfigState = configStore.state;

/**
 * Hand over the <c>tracking</c> object from <c>/api/tenant/config</c>. Passing null or undefined —
 * which is what a tenant without a tracking section and a failed request both produce — turns
 * everything off rather than leaving a previous value standing.
 */
export const setTrackingConfig = configStore.setConfig;

/** The resolved configuration. Reactive: reading it inside an effect re-runs on the server answer. */
export const trackingConfig = (): TrackingConfig => trackingConfigState.config;

export const trackingConfigLoaded = (): boolean => trackingConfigState.loaded;

/** The kill switch alone. Says nothing about whether the rest of the configuration is usable. */
export const trackingEnabled = (): boolean => trackingConfigState.config.enabled === true;

/** The container id, but only when it is shaped like one. Anything else reads as absent. */
export function gtmContainerId(): string | null {
  const id = trackingConfigState.config.gtmContainerId;
  if (!id) return null;
  if (!CONTAINER_ID_PATTERN.test(id)) {
    warnOnce("container", 'GTM container id "' + id + '" is not a container id — tracking stays off.');
    return null;
  }
  return id;
}

/**
 * Whether central measurement is switched on for this tenant. Says nothing about whether there is
 * anywhere to send it — see <c>centralGa4MeasurementId</c>, which is null on LOBAS' own tenant even
 * with the flag on.
 */
export const centralAnalyticsEnabled = (): boolean =>
  trackingConfigState.config.centralAnalyticsEnabled === true;

/**
 * The central property to tag in addition to the tenant's own, or null.
 *
 * <p>Null on every path that must not produce a second tag: the flag off, nothing configured, or a
 * server that answered with the tenant's own property (LOBAS' tenant, where central and own are the
 * same property and a second tag would count every event twice). The flag is re-checked here rather
 * than trusted from the id alone — the two travel together, and a build that read one without the
 * other is the mismatch this function exists to make impossible.</p>
 */
export function centralGa4MeasurementId(): string | null {
  if (!centralAnalyticsEnabled()) return null;

  const id = trackingConfigState.config.centralGa4MeasurementId;
  if (!id) return null;

  if (!MEASUREMENT_ID_PATTERN.test(id)) {
    warnOnce(
      "central-ga4",
      'central GA4 measurement id "' + id + '" is not a measurement id — central measurement stays off.',
    );
    return null;
  }

  // The server already refuses to hand over a central id equal to the tenant's own. Checked again
  // because the cost of the two disagreeing is silent double counting rather than a visible error.
  if (id === trackingConfigState.config.ga4MeasurementId) return null;

  return id;
}

/** The deployment's environment, falling back to the one that assumes the least. */
export const trackingEnvironment = (): TrackingEnvironment =>
  environmentRecognised()
    ? (trackingConfigState.config.environment as TrackingEnvironment)
    : "development";

/**
 * The environment has to be stated, and has to be one we know. An unrecognised value means the
 * server's tracking record was not written the way this code reads it, and the honest response to a
 * configuration nobody has verified is to send nothing — the same stance the API takes when it
 * refuses to start rather than run on a Production DevAuth setting.
 */
export function environmentRecognised(): boolean {
  const env = trackingConfigState.config.environment;
  if (env != null && ENVIRONMENTS.includes(env)) return true;
  if (trackingEnabled()) {
    warnOnce(
      "environment",
      "tracking is enabled but environment is " +
        (env == null ? "not set" : '"' + env + '"') +
        " — expected one of " +
        ENVIRONMENTS.join(", ") +
        ". Nothing will be sent.",
    );
  }
  return false;
}

/**
 * An empty or absent allowlist makes no claim and permits the current host: the server already
 * picked this configuration by hostname. A non-empty list is a statement, and it is binding — it is
 * what keeps a preview or staging copy of the SPA from firing into the production property.
 */
export function hostnameAllowed(hostname?: string): boolean {
  const list = trackingConfigState.config.allowedHostnames;
  if (!list || list.length === 0) return true;
  const host = (
    hostname ?? (typeof window === "undefined" ? "" : window.location.hostname)
  ).toLowerCase();
  if (!host) return false;
  return list.some((h) => h.trim().toLowerCase() === host);
}

/** Whether a vendor is switched on for this deployment. An id without its vendor stays unused. */
export function vendorActive(vendor: TrackingVendor): boolean {
  return trackingConfigState.config.activeVendors?.includes(vendor) ?? false;
}

/**
 * Everything the configuration alone can decide: switched on, a usable container, a known
 * environment, a permitted host. Consent and user group are decided per event, not here.
 */
export function trackingAllowed(): boolean {
  return (
    trackingEnabled() && gtmContainerId() !== null && environmentRecognised() && hostnameAllowed()
  );
}
